import React from 'react';

export const AUTH = {
  pageGradient: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
  pageOverlay:
    'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)',
  cardBg: 'rgba(15, 23, 42, 0.98)',
  cardBorder: 'rgba(255,255,255,0.1)',
  cardShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
  accent: 'linear-gradient(90deg, #2563eb 0%, #4f46e5 100%)',
  titleGradient: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
  subtitle: 'rgba(255,255,255,0.7)',
  caption: 'rgba(255,255,255,0.6)',
  iconRing: 'rgba(37, 99, 235, 0.5)',
  iconBg: 'rgba(37, 99, 235, 0.2)',
  iconGlow: '0 8px 16px rgba(37, 99, 235, 0.3)',
  iconColor: '#2563eb',
  buttonGradient: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
  buttonShadow: '0 4px 15px rgba(37, 99, 235, 0.4)',
  footnoteBg: 'rgba(255,255,255,0.05)',
  footnoteBorder: 'rgba(255,255,255,0.1)',
  errorBg: 'rgba(211, 47, 47, 0.15)',
  errorBorder: 'rgba(211, 47, 47, 0.4)',
  errorText: '#fca5a5',
} as const;

interface AuthCardShellProps {
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
}

export function AuthCardShell({ title, subtitle, children }: AuthCardShellProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden"
      style={{ background: AUTH.pageGradient, padding: 24 }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: AUTH.pageOverlay }}
      />

      <div className="relative w-full" style={{ maxWidth: 480 }}>
        <div
          className="relative overflow-hidden text-center"
          style={{
            borderRadius: 20,
            padding: 'clamp(32px, 5vw, 48px)',
            border: `1px solid ${AUTH.cardBorder}`,
            backgroundColor: AUTH.cardBg,
            backdropFilter: 'blur(10px)',
            boxShadow: AUTH.cardShadow,
          }}
        >
          <div
            className="absolute left-0 right-0 top-0"
            style={{ height: 4, background: AUTH.accent }}
          />

          <div className="flex flex-col items-center" style={{ gap: 24 }}>
            <div className="flex items-center" style={{ gap: 14 }}>
              <img
                src="/encardio.png"
                alt="Encardio Rite Logo"
                style={{ height: 36, width: 'auto', objectFit: 'contain' }}
              />
              <div style={{ width: 1, height: 28, background: 'rgba(255,255,255,0.15)' }} />
              <img
                src="/logo.svg"
                alt="CalibraOne"
                style={{
                  height: 36,
                  width: 36,
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.35))',
                }}
              />
            </div>

            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  lineHeight: 1.4,
                }}
              >
                {title === 'CalibraOne' ? (
                  <>
                    <span
                      style={{
                        background: AUTH.titleGradient,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      Calibra
                    </span>
                    <span style={{ color: '#FDB022' }}>One</span>
                  </>
                ) : (
                  <span
                    style={{
                      background: AUTH.titleGradient,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {title}
                  </span>
                )}
              </h2>
              {subtitle && (
                <p style={{ margin: '4px 0 0', color: AUTH.subtitle, fontSize: '1rem' }}>
                  {subtitle}
                </p>
              )}
            </div>

            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Loading state while Keycloak init / OAuth return runs. */
export function AuthSigningInScreen() {
  return (
    <AuthCardShell title="Signing you in…">
      <div
        className="animate-spin rounded-full"
        style={{
          width: 40,
          height: 40,
          border: '3px solid rgba(37, 99, 235, 0.25)',
          borderTopColor: AUTH.iconColor,
        }}
      />
    </AuthCardShell>
  );
}
